import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { withAppHOC } from '../../../hoc';
import PageHeading from '../../../components/PageHeader/PageHeader';
import OverlayLoader from '../../../components/OverlayLoader/OverlayLoader';
import { Grid, GridItem } from '../../../components/Grid';
import {
  FormContainer,
  Form,
  Fieldset,
  Label,
  TextInput,
  NumberInput,
  Dropdown,
  MessageBox,
  UpdateButton,
  ResetButton,
} from '../../../components/FormElements';
import API from '../../../api';
import { API_RESPONSE_TYPES, PERMISSION_TYPES } from '../../../constants';
import { getReturnUrl } from '../../../utils';

const UsersUpdate = (props) => {
  const {
    appData: { accessType },
  } = props;
  const [isLoading, setIsLoading] = useState(false);
  const [responseStatus, setResponseStatus] = useState('');
  const [responseMessage, setResponseMessage] = useState('');
  const [fullName, setFullname] = useState('');
  const [telegramChatId, setTelegramChatId] = useState('');
  const [email, setEmail] = useState('');
  const [mobileNumber, setMobileNumber] = useState('');
  const [mobileNumberVerificationStatus, setMobileNumberVerificationStatus] =
    useState('Verified');
  const [topupStatus, setTopupStatus] = useState('Active');
  const [withdrawStatus, setWithdrawStatus] = useState('Active');
  const [canSwitchFunds, setCanSwitchFunds] = useState('Active');
  const [tempUserStatus, setTempUserStatus] = useState('Active');
  const [userStatus, setUserStatus] = useState('Active');
  const [complaintRatio, setComplaintRatio] = useState('');
  const [canEditApis, setCanEditApis] = useState('Inactive');
  const [originalFormData, setOriginalFormData] = useState({});
  const { id } = useParams();

  const resetResponseData = () => {
    setIsLoading(false);
    setResponseStatus('');
    setResponseMessage('');
  };

  const resetForm = () => {
    const {
      fullName,
      mobileNumber,
      mobileNumberVerificationStatus,
      topupStatus,
      withdrawStatus,
      canSwitchFunds,
      tempUserStatus,
      userStatus,
      complaintRatio,
      canEditApis,
      telegramChatId,
      email,
    } = originalFormData;
    setFullname(fullName);
    setTelegramChatId(telegramChatId);
    setEmail(email);
    setMobileNumber(mobileNumber);
    setMobileNumberVerificationStatus(mobileNumberVerificationStatus);
    setTopupStatus(topupStatus);
    setWithdrawStatus(withdrawStatus);
    setCanSwitchFunds(canSwitchFunds);
    setTempUserStatus(tempUserStatus);
    setUserStatus(userStatus);
    setComplaintRatio(complaintRatio);
    setCanEditApis(canEditApis);
  };

  const readData = useCallback(() => {
    setIsLoading(true);
    setResponseStatus('');
    setResponseMessage('');
    API.get(`/accounts-management/users/${id}`)
      .then((response) => {
        const { status, message, data } = response.data;
        if (status === API_RESPONSE_TYPES.FAILED) {
          setResponseStatus(status);
          setResponseMessage(message);
        } else {
          const {
            fullName,
            mobileNumber,
            mobileNumberVerificationStatus,
            topupStatus,
            withdrawStatus,
            canSwitchFunds,
            tempUserStatus,
            userStatus,
            complaintRatio,
            canEditApis,
            telegramChatId,
            email,
          } = data[0];
          setFullname(fullName);
          setTelegramChatId(telegramChatId);
          setEmail(email);
          setMobileNumber(mobileNumber);
          setMobileNumberVerificationStatus(mobileNumberVerificationStatus);
          setTopupStatus(topupStatus);
          setWithdrawStatus(withdrawStatus);
          setCanSwitchFunds(canSwitchFunds);
          setTempUserStatus(tempUserStatus);
          setUserStatus(userStatus);
          setComplaintRatio(complaintRatio);
          setCanEditApis(canEditApis);
          setOriginalFormData({
            fullName,
            mobileNumber,
            mobileNumberVerificationStatus,
            topupStatus,
            withdrawStatus,
            canSwitchFunds,
            tempUserStatus,
            userStatus,
            complaintRatio,
            canEditApis,
            telegramChatId,
            email,
          });
        }
      })
      .catch((error) => {
        setResponseStatus(API_RESPONSE_TYPES.FAILED);
        setResponseMessage(error.message);
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [id]);

  const onSubmit = (e) => {
    e.preventDefault();
    setIsLoading(true);
    setResponseStatus('');
    setResponseMessage('');
    const formData = {
      fullName,
      mobileNumber,
      mobileNumberVerificationStatus,
      topupStatus,
      withdrawStatus,
      canSwitchFunds,
      tempUserStatus,
      userStatus,
      complaintRatio: ['V1'].includes(accessType) ? 100 : complaintRatio,
      canEditApis,
      telegramChatId,
      email,
    };
    API.put(`/accounts-management/users/${id}`, formData)
      .then((response) => {
        const { status, message } = response.data;
        setResponseStatus(status);
        setResponseMessage(message);
        if (status === API_RESPONSE_TYPES.SUCCESS) {
          setOriginalFormData(formData);
          setTimeout(() => {
            resetResponseData();
            window.location.href = getReturnUrl(PERMISSION_TYPES.UPDATE);
          }, 3000);
        }
      })
      .catch((error) => {
        setResponseStatus(API_RESPONSE_TYPES.FAILED);
        setResponseMessage(error.message);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  useEffect(() => {
    readData();
  }, [readData]);

  return (
    <>
      <PageHeading {...props} />
      <FormContainer>
        <Form method="POST" action="#" onSubmit={onSubmit}>
          <Fieldset>
            <Label required>Full Name</Label>
            <TextInput
              value={fullName}
              onChange={setFullname}
              placeholder="Please enter full name"
              maxLength={100}
              disabled={isLoading}
            />
          </Fieldset>
          <Grid columns={'calc(50% - 10px) calc(50% - 10px)'}>
            <GridItem>
              <Fieldset>
                <Label required>Mobile Number</Label>
                <NumberInput
                  value={mobileNumber}
                  onChange={setMobileNumber}
                  placeholder="Please enter mobile number"
                  maxLength={10}
                  disabled={true}
                  pattern={`[0-9]*`}
                />
              </Fieldset>
            </GridItem>
            <GridItem>
              <Fieldset>
                <Label required>Mobile Verification Status</Label>
                <Dropdown
                  value={mobileNumberVerificationStatus}
                  onChange={setMobileNumberVerificationStatus}
                  placeholder="Please select mobile verification status"
                  options={[
                    {
                      title: '-- SELECT MOBILE VERIFICATION STATUS --',
                      value: '',
                    },
                    { title: 'Pending', value: 'Pending' },
                    { title: 'Verified', value: 'Verified' },
                  ]}
                  disabled={isLoading}
                />
              </Fieldset>
            </GridItem>
          </Grid>
          <Grid columns={'calc(50% - 10px) calc(50% - 10px)'}>
            <GridItem>
              <Fieldset>
                <Label>Email</Label>
                <TextInput
                  value={email}
                  onChange={setEmail}
                  placeholder="Please enter Email"
                  maxLength={10}
                  disabled={false}
                />
              </Fieldset>
            </GridItem>
            <GridItem>
              <Fieldset>
                <Label required>Telegram ChatID</Label>
                <NumberInput
                  value={telegramChatId}
                  onChange={setTelegramChatId}
                  placeholder="Please enter Telegram ID"
                  maxLength={25}
                  disabled={false}
                  pattern={`[0-9]*`}
                />
              </Fieldset>
            </GridItem>
          </Grid>
          <Grid
            columns={
              'calc(25% - 15px) calc(25% - 15px) calc(25% - 15px) calc(25% - 15px)'
            }
          >
            <GridItem>
              <Fieldset>
                <Label required>Wallet Topup Status</Label>
                <Dropdown
                  placeholder="Please select wallet topup status"
                  value={topupStatus}
                  onChange={setTopupStatus}
                  options={[
                    {
                      title: '-- SELECT WALLET TOPUP STATUS --',
                      value: '',
                    },
                    {
                      title: 'Active',
                      value: 'Active',
                    },
                    {
                      title: 'Inactive',
                      value: 'Inactive',
                    },
                  ]}
                  disabled={isLoading}
                />
              </Fieldset>
            </GridItem>
            <GridItem>
              <Fieldset>
                <Label required>Withdraw Funds Status</Label>
                <Dropdown
                  placeholder="Please select withdraw funds status"
                  value={withdrawStatus}
                  onChange={setWithdrawStatus}
                  options={[
                    {
                      title: '-- SELECT WITHDRAW FUNDS STATUS --',
                      value: '',
                    },
                    {
                      title: 'Active',
                      value: 'Active',
                    },
                    {
                      title: 'Inactive',
                      value: 'Inactive',
                    },
                  ]}
                  disabled={isLoading}
                />
              </Fieldset>
            </GridItem>
            <GridItem>
              <Fieldset>
                <Label required>Switch Funds Status</Label>
                <Dropdown
                  placeholder="Please select switch funds status"
                  value={canSwitchFunds}
                  onChange={setCanSwitchFunds}
                  options={[
                    {
                      title: '-- SELECT SWITCH FUNDS STATUS --',
                      value: '',
                    },
                    {
                      title: 'Active',
                      value: 'Active',
                    },
                    {
                      title: 'Inactive',
                      value: 'Inactive',
                    },
                  ]}
                  disabled={isLoading}
                />
              </Fieldset>
            </GridItem>
            <GridItem>
              <Fieldset>
                <Label required>APIS Edit Status</Label>
                <Dropdown
                  placeholder="Please select apis edit status"
                  value={canEditApis}
                  onChange={setCanEditApis}
                  options={[
                    {
                      title: '-- SELECT APIS EDIT STATUS --',
                      value: '',
                    },
                    {
                      title: 'Active',
                      value: 'Active',
                    },
                    {
                      title: 'Inactive',
                      value: 'Inactive',
                    },
                  ]}
                  disabled={isLoading}
                />
              </Fieldset>
            </GridItem>
          </Grid>

          {!['V1'].includes(accessType) && (
            <Fieldset>
              <Label required>Complaint Ratio</Label>
              <NumberInput
                value={complaintRatio}
                onChange={setComplaintRatio}
                placeholder="Please enter complaint ratio"
                disabled={isLoading}
              />
            </Fieldset>
          )}
          <Fieldset>
            <Label required>User Status</Label>
            <Dropdown
              placeholder="Please select user status"
              value={userStatus}
              onChange={setUserStatus}
              options={[
                {
                  title: '-- SELECT USER STATUS --',
                  value: '',
                },
                {
                  title: 'Active',
                  value: 'Active',
                },
                {
                  title: 'Inactive',
                  value: 'Inactive',
                },
              ]}
              disabled={isLoading}
            />
          </Fieldset>
          {responseStatus && (
            <Fieldset>
              <MessageBox status={responseStatus} message={responseMessage} />
            </Fieldset>
          )}
          <Fieldset>
            <UpdateButton disabled={isLoading} type="submit">
              Update
            </UpdateButton>
            <ResetButton disabled={isLoading} type="button" onClick={resetForm}>
              Reset
            </ResetButton>
          </Fieldset>
        </Form>
        <OverlayLoader showLoader={isLoading} />
      </FormContainer>
    </>
  );
};

export default withAppHOC(UsersUpdate);
